import { Component, Input, OnDestroy, OnInit, Inject, Output, EventEmitter,
    ChangeDetectorRef, OnChanges, HostListener
} from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { ColumnSeries } from '@amcharts/amcharts4/charts';

import { BehaviorSubject, map, of, Subscription, switchMap, tap } from 'rxjs';
import * as am4core from '@amcharts/amcharts4/core';

import { DropdownTypesEnum } from '../../../Forms/enums/dropdown-types.enum';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { DataPointModel } from '../../models/data-point.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ChartMetadataModel } from '../../models/chart-metadata.model';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { userAppPreferencesDefault } from '../../../../Core/Users/data/user-app-preferences-default';
import { ReportSeriesModel } from '../../../Reports/models/report-series.model';
import { ChartModel } from '../../models/chart.model';
import { CategoryValueXYChart } from '../../models/category-value-x-y-chart.model';
import { CategoryValueXYChartOptions } from '../../models/barChart/category-value-x-y-chart-options.model';
import { ReportBlockTypeEnum } from '../../../Reports/enums/report-block-type.enum';
import { ChartMetricsEnum } from '../../../Reports/enums/chart-metrics.enum';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { OutletsPublicApi } from '../../../../api/targeting/outlets-public.api';
import { BaseJournodexEntityModel } from '../../../../Discover/Targeting/models/base-journodex-entity.model';
import { JournodexEntityFactory } from '../../../../Discover/Targeting/factories/journodex-entity.factory';
import { EntityTypeDefaultImageEnum } from '../../../Misc/enums/entity-type-default-image.enum';
import { TwitterAvatarSize } from '../../../Misc/pipes/TwitterAvatarSize/twitter-avatar-size.pipe';
import { EmploymentModel } from '../../../../Discover/Targeting/models/employment.model';
import { ContactModel } from '../../../TargetingShared/model/contact.model';
import { OutletModel } from '../../../../Discover/Targeting/models/outlet.model';
import { CirculationType } from '../../../../Discover/Targeting/models/circulation.model';
import { LabelDisplayOptionsEnum } from '../../../Misc/enums/label-display-options.enum';
import { ContactsPublicApi } from '../../../../api/targeting/contacts-public.api';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { MADChartOptionsEnum } from '../../../../Monitor/Shared/enums/mad-chart-options.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { ChartStylesModel } from '../../models/chart-styles.model';
import { chartColors } from '../../enums/chart-colors.enum';
import { attributeTypesEnum } from '../../../Misc/enums/attribute-types.enum';
import { ChartDisplayElementModel } from '../../../Reports/models/chart-display-element.model';
import { ProfileTakeOverInterface } from '../../../TargetingShared/interfaces/profile-take-over.interface';
import { reportsMockAuthorData, reportsMockOutletData } from '../../../Reports/services/report-series-preview.service';
import { ChartSpecificTypesEnum } from '../../../Reports/enums/chart-specific-types.enum';

@Component({
    selector: 'agpr-top-entities-chart',
    templateUrl: './top-entities-chart.component.html',
    styleUrls: ['./top-entities-chart.component.scss'],
    standalone: false
})
export class TopEntitiesChartComponent implements OnInit, OnDestroy, OnChanges {
    readonly buttonTypes = ButtonTypesEnum;
    readonly dropdownTypes = DropdownTypesEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly options: { label: string; value: ChartMetricsEnum }[] = [
        {
            label: `Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.VOLUME}`,
            value: ChartMetricsEnum.VOLUME
        },
        {
            label: `Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.IMPRESSIONS}`,
            value: ChartMetricsEnum.IMPRESSIONS
        },
        {
            label: `Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.AVE}`,
            value: ChartMetricsEnum.ave
        },
        {
            label: `Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.DOMAIN_AUTHORITY}`,
            value: ChartMetricsEnum.domainAuthority
        },
    ];
    readonly defaultChartColors = userAppPreferencesDefault.reportsPrefs.defaultChartColors;
    readonly twitterAvatarSizes = TwitterAvatarSize;
    readonly entityTypes = EntityType;
    readonly circulationType = CirculationType;
    readonly messageSeverities = messageSeverityEnum;
    readonly searchCriteriaQuerySentimentsEnum = SearchCriteriaQuerySentimentsEnum;
    readonly sentimentsToChartColorsMap = new Map<SearchCriteriaQuerySentimentsEnum, chartColors>([
        [SearchCriteriaQuerySentimentsEnum.positive, chartColors.agprPositive],
        [SearchCriteriaQuerySentimentsEnum.negative, chartColors.agprNegative],
    ]);
    readonly attributeTypesEnum = attributeTypesEnum;

    @Input() id: string = 'topEntitiesMADChart';
    @Input() displayElement: ChartDisplayElementModel;
    @Input() series: ReportSeriesModel = null;
    @Input() entityType: EntityType = null;
    @Input() metricSelectable: boolean;
    @Input() bigProfileImage: boolean;
    @Input() width: number;
    @Input() height: number;
    @Input() accentColor: string;
    @Input() disableViewMentionLink: boolean;
    @Input() preview: boolean;
    @Input() canViewProfileDetail: boolean;
    @Input() titleAndMetadataHeight: number = 280;

    @Output() seriesChange: EventEmitter<{
        newSeries: ReportSeriesModel;
        entityType: EntityType;
        sentimentType?: SearchCriteriaQuerySentimentsEnum;
    }> = new EventEmitter();

    profileLoading: boolean = false;
    noData: boolean = false;
    initialSelection: boolean = false;
    headerHeight: number = 15;
    sourceRouterLink: string;
    sourceRouterLinkQueryParams: any;

    chart: ChartModel;
    dataPoints: DataPointModel[] = [];
    selectedMetric: DropdownOptionModel<ChartMetricsEnum>;
    selectedEntity: BaseJournodexEntityModel;
    selectedEmployment: EmploymentModel;
    sentimentType: SearchCriteriaQuerySentimentsEnum = null;
    contactEntity: ContactModel;
    outletEntity: OutletModel;
    bio: string;

    imageDefault = EntityTypeDefaultImageEnum;
    similarWebTooltip: string;

    mediaTypeDisplay: string;
    languagesDisplay: string;
    languagesDisplayTooltip: string;
    geographyDisplay: string;

    private selectedEntityId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    defaultSelectedData: DataPointModel = null;
    public subscriptions: Subscription[] = [];

    constructor(
        public localizationService: LocalizationService,
        public outletsPublicApi: OutletsPublicApi,
        public contactsPublicApi: ContactsPublicApi,
        public journodexEntityFactory: JournodexEntityFactory,
        public cd: ChangeDetectorRef,
        @Inject('ProfileTakeOverInterface') public targetingRhpDialogService: ProfileTakeOverInterface,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {}

    ngOnInit(): void {
        this.initSourceRouterLink();
    }

    ngOnChanges(): void {
        this.similarWebTooltip = this.localizationService.transform(
            'Common.audienceReach.similarWeb.tooltip',
            {src: this.appBaseHref + 'assets/images/SimilarWeb-logo-with-text.svg'}
        );
        this.selectedMetric = this.options.find(opt => opt.value === this.series.metric);
        const chartStyles = new ChartStylesModel();
        chartStyles.colorMap['barColor'] = this.accentColor || this.displayElement?.styles.colorMap['barColor'];
        if (this.series.mentions.filters.searchCriteria.query.sentiments?.length > 0) {
            this.sentimentType =  this.series.mentions.filters.searchCriteria.query.sentiments[0].id;
            chartStyles.colorMap['barColor'] = this.sentimentsToChartColorsMap.get(this.sentimentType);
        }
        chartStyles.columnHeightThreshold = 12;
        chartStyles.customPadding = {
            left: 10,
            top: 0,
            right: 0,
            bottom: 0
        };

        const displaySettings = this.displayElement?.chartHeaderSettings;
        this.headerHeight = 15;
        if (displaySettings) {
            if (displaySettings.title) {
                this.headerHeight += 30;
            }
            if (displaySettings.dateRange || displaySettings.sourceLink) {
                this.headerHeight += 22;
            }
        }

        this.chart = new CategoryValueXYChart(
            [this.series],
            {
                chartSpecificType: this.displayElement?.chartSpecificType || ChartSpecificTypesEnum.BAR,
                categoryLabelStackedOnTop: true,
                hideLegend: true,
                noActionOnColumnClick: true,
                styles: chartStyles,
            } as CategoryValueXYChartOptions,
            {
                type: this.entityType === EntityType.OUTLET ? ReportBlockTypeEnum.TOP_10_OUTLETS : ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME,
                enableDataDisplaySettings: true,
                labelDisplayOptions: this.displayElement?.labelDisplayOptions || [LabelDisplayOptionsEnum.count]
            } as ChartMetadataModel,
            [{}]
        );
        this.subscriptions.push(
            this.series.dataProvider.pipe(
                map(data => {
                    return data ?? [];
                })
            ).subscribe(data => {
                this.dataPoints = data;
                let selectedChartData = null;
                if (data) {
                    selectedChartData = data.find(x => x.uuid !== null && x.uuid !== undefined);
                    if (!selectedChartData) {
                        this.defaultSelectedData = data[0];
                    } else {
                        this.defaultSelectedData = selectedChartData;
                    }
                }
                this.selectedEntityId$.next(this.defaultSelectedData?.uuid as string ?? null);
                this.initialSelection = false;
            }),
            this.selectedEntityId$.asObservable().pipe(
                switchMap(id => {
                    this.profileLoading = true;
                    if (!id) {
                        return of(null);
                    }

                    if (this.entityType === EntityType.OUTLET) {
                        if (this.preview) {
                            return of(reportsMockOutletData);
                        } else {
                            return this.outletsPublicApi.getOutlet(id);
                        }
                    }
                    if (this.entityType === EntityType.CONTACT) {
                        if (this.preview) {
                            return of(reportsMockAuthorData);
                        } else {
                            return this.contactsPublicApi.getContact(id);
                        }
                    }
                }),
                switchMap(entityModel => {
                    if (entityModel) {
                        if (this.entityType === EntityType.OUTLET) {
                            return this.journodexEntityFactory.createOutlet(entityModel);
                        }
                        if (this.entityType === EntityType.CONTACT) {
                            return this.journodexEntityFactory.createContact(entityModel);
                        }
                        return of(null);
                    } else {
                        return of(null);
                    }
                })
            ).subscribe(entity => {
                this.profileLoading = false;
                this.selectedEntity = entity;
                this.selectedEmployment = null;
                if (this.selectedEntity) {
                    if (this.entityType === EntityType.OUTLET) {
                        this.outletEntity = <OutletModel>entity;
                        this.mediaTypeDisplay = this.outletEntity.mediaTypes?.map(
                            mediaType => this.localizationService.transform(mediaType.displayString)
                        ).join(', ');

                        this.languagesDisplay = this.outletEntity.languages?.map(language => {
                            if (language.name.startsWith('Language.')) {
                                return language.name.substring('Language.'.length);
                            }
                            return language.name;
                        }).join(', ');

                        this.languagesDisplayTooltip = this.outletEntity.languages?.map(language =>
                            this.localizationService.transform(language.name)
                        ).join(', ');

                        this.geographyDisplay = this.outletEntity.geography?.displayString?.trim();

                    }
                    if (this.entityType === EntityType.CONTACT) {
                        this.contactEntity = <ContactModel>entity;
                        this.selectedEmployment = this.contactEntity.selectedEmployment;
                        const contactGeography = this.contactEntity?.geography?.displayString?.trim();
                        const outletGeography = this.selectedEmployment?.outlet?.geography?.displayString?.trim();
                        this.geographyDisplay = contactGeography || outletGeography || '';
                    }
                }
                this.cd.detectChanges();
            }),
            this.chart.ready.subscribe(value => {
                if (value) {
                    this.chart.amchart.series.each((series: ColumnSeries, index: number) => {
                        (series as ColumnSeries).columns.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;
                        series.columns.template.togglable = true;
                        const focusState = series.columns.template.states.create('active');
                        focusState.properties.fill = am4core.color(series.columns.template.fill.toString()).lighten(0.4);
                        focusState.properties.strokeWidth = 2;
                        (series as ColumnSeries).columns.template.events.on('hit', event => {
                            if (event.target.dataItem.dataContext['uuid']) {
                                this.selectedEntityId$.next(event.target.dataItem.dataContext['uuid']);
                            } else {
                                this.selectedEntityId$.next(null);
                            }
                            series.columns.each(function(column) {
                                if (column !== event.target) {
                                    column.setState('default');
                                    column.isActive = false;
                                }
                            });

                        });

                        (series as ColumnSeries).columns.template.events.on('ready', event => {
                            if (
                                !this.initialSelection &&
                                ((
                                    this.defaultSelectedData?.uuid
                                    && event.target.dataItem.dataContext['uuid']
                                    && event.target.dataItem.dataContext['uuid'] === this.defaultSelectedData.uuid
                                ) ||
                                (
                                    this.defaultSelectedData?.label
                                    && event.target.dataItem.dataContext['label']
                                    && event.target.dataItem.dataContext['label'] === this.defaultSelectedData.label
                                ))
                            ) {
                                event.target.dispatchImmediately('hit');
                                this.initialSelection = true;
                            }
                        });

                    });
                }
            })
        );
    }

    initSourceRouterLink(): void {
        if (this.displayElement?.series?.[0]) {
            const urlParts = this.displayElement.series[0].getMentionSeriesUrl()?.split('?filters=');
            this.sourceRouterLink = '/' + urlParts?.[0];
            this.sourceRouterLinkQueryParams = { filters: urlParts?.[1] };
        }
    }

    ngOnDestroy(): void {
        this.subscriptions?.forEach(sub => sub.unsubscribe());
    }

    onMetricChange() {
        this.selectedEntity = null;
        const newSeries = this.series.clone();
        newSeries.metric = this.selectedMetric.value;
        this.seriesChange.emit({newSeries: newSeries, entityType: this.entityType, sentimentType: this.sentimentType});
        this.cd.detectChanges();
    }

    openFullEntityProfile() {
        this.targetingRhpDialogService.openTakeOver({
            inputs: {
                entityType: this.selectedEntity.entityType,
                contactId: this.selectedEntity.id,
                outletId: this.selectedEntity.id
            }
        });
    }

    updateNoData(noData: boolean) {
        this.noData = noData;
    }
}
